import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Polyline, CircleMarker, Tooltip, GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import { ALGORITHMS, ALGO_KEYS_OUTLIER, ALGO_KEYS_SMOOTH, runPipeline, computeMetrics } from './smoothingAlgorithms';
import { Plus, Trash2, Eye, EyeOff, ChevronDown, ChevronUp, Info } from './Icons';
import AccuracyLegend from './AccuracyLegend';
import Scrubber from './Scrubber';
import './DevPage.css';

const DEFAULT_COLORS = ['#fc5f21','#2196F3','#FF9800','#E91E63','#9C27B0','#00BCD4','#FF5722','#795548','#607D8B','#CDDC39'];

const getAccuracyColor = (accuracy, minAcc, maxAcc) => {
  if (!accuracy && accuracy !== 0) return '#999999';
  const range = maxAcc - minAcc;
  if (range === 0) return '#70C314';
  const normalized = (accuracy - minAcc) / range;
  if (normalized < 0.25) return '#70C314';
  if (normalized < 0.5) return '#FAEB36';
  if (normalized < 0.75) return '#FFA500';
  if (normalized < 0.9) return '#E81416';
  return '#111111';
};

const MapResizeHandler = () => {
  const map = useMap();
  useEffect(() => {
    const container = map.getContainer();
    const observer = new ResizeObserver(() => {
      map.invalidateSize();
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [map]);
  return null;
};

const MapFitter = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.isValid()) {
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [bounds, map]);
  return null;
};

function defaultParamsFor(key) {
  const algo = ALGORITHMS[key];
  if (!algo) return {};
  const params = {};
  algo.params.forEach(p => { params[p.key] = p.default; });
  return params;
}

/* ============ Collapsible Section ============ */
const Section = ({ title, defaultOpen = true, badge, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`dev-section ${open ? 'open' : 'closed'}`}>
      <div className="section-header" onClick={() => setOpen(o => !o)}>
        <span className="section-title">{title}</span>
        {badge !== undefined && <span className="section-badge">{badge}</span>}
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </div>
      {open && <div className="section-body">{children}</div>}
    </div>
  );
};

/* ============ Help Panel ============ */
const HelpPanel = ({ lines }) => (
  <div className="help-panel">
    {lines.map((line, i) => (
      <div key={i} className={`help-line ${line.startsWith('  ') ? 'indent' : ''}`}>{line}</div>
    ))}
  </div>
);

/* ============ Insert Divider ============ */
const InsertPoint = ({ onInsert }) => (
  <div className="pipeline-insert-point">
    <div className="insert-line" />
    <button onClick={() => onInsert('outlier')} className="insert-btn outlier" title="Insert outlier filter here">
      <Plus size={9} />
    </button>
    <button onClick={() => onInsert('smooth')} className="insert-btn smooth" title="Insert smoothing filter here">
      <Plus size={9} />
    </button>
    <div className="insert-line" />
  </div>
);

/* ============ Main Component ============ */
const KMZ_STYLE_DEFAULTS = {
  line: { color: '#8B5CF6', weight: 3, opacity: 0.7 },
  polygon: { color: '#8B5CF6', weight: 2, opacity: 0.6, fillColor: '#8B5CF6', fillOpacity: 0.15 },
  point: { radius: 5, color: '#8B5CF6', fillColor: '#8B5CF6', fillOpacity: 0.8, weight: 2 },
};

const DevPage = ({ activities, activityIds, activityColors, kmzOverlays }) => {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [devColors, setDevColors] = useState({});
  const [markerSize, setMarkerSize] = useState(3);
  const [pipeline, setPipeline] = useState([
    { key: 'speedGate', params: defaultParamsFor('speedGate') },
    { key: 'kalmanFilter', params: defaultParamsFor('kalmanFilter') },
  ]);
  const [showRawTrace, setShowRawTrace] = useState(true);
  const [showSmoothedTrace, setShowSmoothedTrace] = useState(true);
  const [showRawPoints, setShowRawPoints] = useState(true);
  const [showSmoothedPoints, setShowSmoothedPoints] = useState(false);
  const [showAccuracyColors, setShowAccuracyColors] = useState(false);
  const [accuracyBounds, setAccuracyBounds] = useState({ min: 2, max: 50 });
  const [expandedHelp, setExpandedHelp] = useState(new Set());

  // Time scrubber state (0-1000 range)
  const [timeHead, setTimeHead] = useState(1000);
  const [timeTail, setTimeTail] = useState(0);

  // Initialize colors from parent + auto-select first activity
  useEffect(() => {
    if (activityIds.length > 0) {
      const colors = {};
      activityIds.forEach((id, i) => {
        colors[id] = activityColors[id] || DEFAULT_COLORS[i % DEFAULT_COLORS.length];
      });
      setDevColors(prev => {
        const merged = { ...colors };
        Object.keys(prev).forEach(k => { if (activityIds.includes(k)) merged[k] = prev[k]; });
        return merged;
      });
    }
  }, [activityIds, activityColors]);

  useEffect(() => {
    if (selectedIds.size === 0 && activityIds.length > 0) {
      setSelectedIds(new Set([activityIds[0]]));
    }
  }, [activityIds, selectedIds.size]);

  const getColor = useCallback((id) => devColors[id] || activityColors[id] || '#fc5f21', [devColors, activityColors]);

  // Points by activity (sorted by time)
  const pointsByActivity = useMemo(() => {
    const map = {};
    activityIds.forEach(id => {
      const pts = activities.filter(p => p.activityId === id);
      pts.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      map[id] = pts;
    });
    return map;
  }, [activities, activityIds]);

  // Process pipeline for each selected activity (full data, before time windowing)
  const processedData = useMemo(() => {
    const result = {};
    for (const id of selectedIds) {
      const raw = pointsByActivity[id] || [];
      if (raw.length === 0) continue;
      try {
        const smoothed = pipeline.length > 0 ? runPipeline(raw, pipeline) : raw.map(p => ({ ...p }));
        result[id] = { raw, smoothed };
      } catch (err) {
        console.error(`Pipeline error for ${id}:`, err);
        result[id] = { raw, smoothed: [] };
      }
    }
    return result;
  }, [selectedIds, pointsByActivity, pipeline]);

  // Global time range across all selected activities
  const globalTimeRange = useMemo(() => {
    let min = Infinity, max = -Infinity;
    for (const id of selectedIds) {
      const d = processedData[id];
      if (!d || d.raw.length === 0) continue;
      const first = new Date(d.raw[0].timestamp).getTime();
      const last = new Date(d.raw[d.raw.length - 1].timestamp).getTime();
      if (first < min) min = first;
      if (last > max) max = last;
    }
    if (!isFinite(min)) return { min: 0, max: 0 };
    return { min, max };
  }, [selectedIds, processedData]);

  // Convert slider values to time window
  const timeWindow = useMemo(() => {
    const range = globalTimeRange.max - globalTimeRange.min;
    if (range <= 0) return { head: Infinity, tail: 0 };
    return {
      head: globalTimeRange.min + (timeHead / 1000) * range,
      tail: globalTimeRange.min + (timeTail / 1000) * range,
    };
  }, [timeHead, timeTail, globalTimeRange]);

  // Filter displayed data by time window
  const displayData = useMemo(() => {
    const result = {};
    for (const id of selectedIds) {
      const d = processedData[id];
      if (!d) continue;
      const rawFiltered = d.raw.filter(p => {
        const t = new Date(p.timestamp).getTime();
        return t >= timeWindow.tail && t <= timeWindow.head;
      });
      const smoothedFiltered = d.smoothed.filter(p => {
        const t = new Date(p.timestamp).getTime();
        return t >= timeWindow.tail && t <= timeWindow.head;
      });
      result[id] = { raw: rawFiltered, smoothed: smoothedFiltered };
    }
    return result;
  }, [selectedIds, processedData, timeWindow]);

  // Combined metrics (on full processed data, not time-windowed)
  const allRawPoints = useMemo(() => {
    let pts = [];
    for (const id of selectedIds) {
      const d = processedData[id];
      if (d) pts = pts.concat(d.raw);
    }
    return pts;
  }, [selectedIds, processedData]);

  const allSmoothedPoints = useMemo(() => {
    let pts = [];
    for (const id of selectedIds) {
      const d = processedData[id];
      if (d) pts = pts.concat(d.smoothed);
    }
    return pts;
  }, [selectedIds, processedData]);

  const rawMetrics = useMemo(() => computeMetrics(allRawPoints), [allRawPoints]);
  const smoothedMetrics = useMemo(() => computeMetrics(allSmoothedPoints), [allSmoothedPoints]);

  // Map bounds (based on full data so map stays stable during scrubbing)
  const mapBounds = useMemo(() => {
    const allPts = [];
    for (const id of selectedIds) {
      const d = processedData[id];
      if (d) d.raw.forEach(p => allPts.push([p.latitude, p.longitude]));
    }
    if (allPts.length === 0) return null;
    return L.latLngBounds(allPts);
  }, [selectedIds, processedData]);

  const center = useMemo(() => {
    if (allRawPoints.length > 0) return [allRawPoints[0].latitude, allRawPoints[0].longitude];
    return [37.77, -122.42];
  }, [allRawPoints]);

  // --- Selection helpers ---
  const toggleId = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelectedIds(new Set(activityIds));
  const selectNone = () => setSelectedIds(new Set());

  // --- Pipeline management ---
  const insertStage = (atIdx, category) => {
    const keys = category === 'outlier' ? ALGO_KEYS_OUTLIER : ALGO_KEYS_SMOOTH;
    const key = keys[0];
    setPipeline(prev => {
      const next = [...prev];
      next.splice(atIdx, 0, { key, params: defaultParamsFor(key) });
      return next;
    });
  };

  const removeStage = (idx) => setPipeline(prev => prev.filter((_, i) => i !== idx));

  const updateStageKey = (idx, newKey) => {
    setPipeline(prev => prev.map((s, i) => i === idx ? { key: newKey, params: defaultParamsFor(newKey) } : s));
  };

  const updateStageParam = (idx, paramKey, value) => {
    setPipeline(prev => prev.map((s, i) =>
      i === idx ? { ...s, params: { ...s.params, [paramKey]: value } } : s
    ));
  };

  const toggleHelp = (idx) => {
    setExpandedHelp(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const handleAccuracyBoundsChange = (min, max) => {
    setAccuracyBounds({ min, max });
  };

  // Reset scrubber when selection changes
  useEffect(() => {
    setTimeHead(1000);
    setTimeTail(0);
  }, [selectedIds.size]);

  if (activityIds.length === 0) {
    return (
      <div className="dev-page-empty">
        <h2>Algorithm Dev Sandbox</h2>
        <p>No activities loaded. Go to &quot;Query &amp; Select&quot; to fetch data, then plot activities before using the Dev tab.</p>
      </div>
    );
  }

  const fmtDist = (km) => km < 1 ? `${(km * 1000).toFixed(0)} m` : `${km.toFixed(2)} km`;
  const fmtSpeed = (v) => `${v.toFixed(1)} km/h`;
  const pctChange = (raw, smooth) => {
    if (raw === 0) return '';
    const pct = ((smooth - raw) / raw * 100).toFixed(1);
    return pct > 0 ? `(+${pct}%)` : `(${pct}%)`;
  };

  return (
    <div className="dev-page">
      <div className="dev-sidebar">
        <h2>Algorithm Sandbox</h2>

        {/* ======= Activities ======= */}
        <Section title="Activities" badge={`${selectedIds.size}/${activityIds.length}`}>
          <div className="activity-list">
            {activityIds.map(id => {
              const ptCount = (pointsByActivity[id] || []).length;
              return (
                <div key={id} className={`activity-row ${selectedIds.has(id) ? 'selected' : ''}`}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(id)}
                    onChange={() => toggleId(id)}
                    className="activity-cb"
                  />
                  <label className="color-swatch-dev">
                    <span className="color-preview-dev" style={{ background: getColor(id) }} />
                    <input
                      type="color"
                      value={getColor(id)}
                      onChange={(e) => setDevColors(prev => ({ ...prev, [id]: e.target.value }))}
                      className="color-input-hidden-dev"
                    />
                  </label>
                  <span className="activity-name" title={id}>{id.substring(0, 14)}...</span>
                  <span className="activity-pts">{ptCount}</span>
                </div>
              );
            })}
          </div>
          <div className="activity-actions">
            <button onClick={selectAll} className="mini-btn">Select All</button>
            <button onClick={selectNone} className="mini-btn">Select None</button>
          </div>
        </Section>

        {/* ======= Display Settings ======= */}
        <Section title="Display" defaultOpen={false}>
          <div className="display-settings">
            <div className="setting-row">
              <span className="setting-label">Marker Size</span>
              <span className="setting-value">{markerSize}</span>
            </div>
            <input
              type="range" min={1} max={10} step={0.5} value={markerSize}
              onChange={(e) => setMarkerSize(parseFloat(e.target.value))}
              className="param-slider"
            />
          </div>
          <div className="toggle-grid">
            <button className={`toggle-btn ${showRawTrace ? 'on' : ''}`} onClick={() => setShowRawTrace(p => !p)}>
              {showRawTrace ? <Eye size={12} /> : <EyeOff size={12} />} Raw Trace
            </button>
            <button className={`toggle-btn ${showSmoothedTrace ? 'on' : ''}`} onClick={() => setShowSmoothedTrace(p => !p)}>
              {showSmoothedTrace ? <Eye size={12} /> : <EyeOff size={12} />} Smoothed
            </button>
            <button className={`toggle-btn ${showRawPoints ? 'on' : ''}`} onClick={() => setShowRawPoints(p => !p)}>
              {showRawPoints ? <Eye size={12} /> : <EyeOff size={12} />} Raw Pts
            </button>
            <button className={`toggle-btn ${showSmoothedPoints ? 'on' : ''}`} onClick={() => setShowSmoothedPoints(p => !p)}>
              {showSmoothedPoints ? <Eye size={12} /> : <EyeOff size={12} />} Smooth Pts
            </button>
            <button
              className={`toggle-btn accuracy ${showAccuracyColors ? 'on' : ''}`}
              onClick={() => setShowAccuracyColors(p => !p)}
              style={{ gridColumn: '1 / -1' }}
            >
              {showAccuracyColors ? <Eye size={12} /> : <EyeOff size={12} />} GPS Accuracy Coloring
            </button>
          </div>
        </Section>

        {/* ======= Pipeline ======= */}
        <Section title="Pipeline" badge={pipeline.length}>
          {pipeline.length === 0 && (
            <>
              <div className="pipeline-empty">No stages — raw data will be shown as-is.</div>
              <InsertPoint onInsert={(cat) => insertStage(0, cat)} />
            </>
          )}
          {pipeline.map((stage, idx) => {
            const algo = ALGORITHMS[stage.key];
            if (!algo) return null;
            const isOutlier = algo.category === 'outlier';
            return (
              <React.Fragment key={idx}>
                <InsertPoint onInsert={(cat) => insertStage(idx, cat)} />
                <div className={`pipeline-stage ${isOutlier ? 'outlier' : 'smooth'}`}>
                  <div className="stage-header">
                    <span className={`stage-num ${isOutlier ? 'outlier' : ''}`}>{idx + 1}</span>
                    <select
                      value={stage.key}
                      onChange={(e) => updateStageKey(idx, e.target.value)}
                      className="stage-algo-select"
                    >
                      <optgroup label="Outlier Detection">
                        {ALGO_KEYS_OUTLIER.map(k => <option key={k} value={k}>{ALGORITHMS[k].name}</option>)}
                      </optgroup>
                      <optgroup label="Smoothing">
                        {ALGO_KEYS_SMOOTH.map(k => <option key={k} value={k}>{ALGORITHMS[k].name}</option>)}
                      </optgroup>
                    </select>
                    <button onClick={() => toggleHelp(idx)} className={`stage-info ${expandedHelp.has(idx) ? 'active' : ''}`} title="Info">
                      <Info size={14} />
                    </button>
                    <button onClick={() => removeStage(idx)} className="stage-remove" title="Remove">
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="stage-description">{algo.description}</div>
                  {expandedHelp.has(idx) && algo.help && <HelpPanel lines={algo.help} />}
                  <div className="stage-params">
                    {algo.params.map(paramDef => (
                      <div key={paramDef.key} className="param-row">
                        {paramDef.type === 'checkbox' ? (
                          <label className="param-checkbox">
                            <input
                              type="checkbox"
                              checked={stage.params[paramDef.key] ?? paramDef.default}
                              onChange={(e) => updateStageParam(idx, paramDef.key, e.target.checked)}
                            />
                            <span>{paramDef.label}</span>
                          </label>
                        ) : (
                          <>
                            <div className="param-label-row">
                              <span className="param-name">{paramDef.label}</span>
                              <span className="param-value">{stage.params[paramDef.key] ?? paramDef.default}</span>
                            </div>
                            <input
                              type="range"
                              min={paramDef.min}
                              max={paramDef.max}
                              step={paramDef.step}
                              value={stage.params[paramDef.key] ?? paramDef.default}
                              onChange={(e) => updateStageParam(idx, paramDef.key, parseFloat(e.target.value))}
                              className="param-slider"
                            />
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </React.Fragment>
            );
          })}
          {pipeline.length > 0 && (
            <InsertPoint onInsert={(cat) => insertStage(pipeline.length, cat)} />
          )}
        </Section>

        {/* ======= Metrics ======= */}
        <Section title="Metrics" defaultOpen={true} badge={selectedIds.size > 0 ? `${selectedIds.size} traces` : undefined}>
          {selectedIds.size === 0 ? (
            <div className="pipeline-empty">Select an activity to see metrics.</div>
          ) : (
            <table className="metrics-table">
              <thead>
                <tr><th></th><th>Raw</th><th>Processed</th></tr>
              </thead>
              <tbody>
                <tr>
                  <td>Points</td>
                  <td>{rawMetrics.pointCount}</td>
                  <td>{smoothedMetrics.pointCount} {pctChange(rawMetrics.pointCount, smoothedMetrics.pointCount)}</td>
                </tr>
                <tr>
                  <td>Distance</td>
                  <td>{fmtDist(rawMetrics.totalDistKm)}</td>
                  <td>{fmtDist(smoothedMetrics.totalDistKm)} {pctChange(rawMetrics.totalDistKm, smoothedMetrics.totalDistKm)}</td>
                </tr>
                <tr>
                  <td>Avg Speed</td>
                  <td>{fmtSpeed(rawMetrics.avgSpeedKmh)}</td>
                  <td>{fmtSpeed(smoothedMetrics.avgSpeedKmh)}</td>
                </tr>
                <tr>
                  <td>Max Speed</td>
                  <td>{fmtSpeed(rawMetrics.maxSpeedKmh)}</td>
                  <td>{fmtSpeed(smoothedMetrics.maxSpeedKmh)}</td>
                </tr>
                <tr>
                  <td>Max Jump</td>
                  <td>{rawMetrics.maxSegDistM.toFixed(1)} m</td>
                  <td>{smoothedMetrics.maxSegDistM.toFixed(1)} m</td>
                </tr>
                <tr>
                  <td>Smoothness</td>
                  <td>{rawMetrics.smoothness.toFixed(1)}°</td>
                  <td>{smoothedMetrics.smoothness.toFixed(1)}°</td>
                </tr>
              </tbody>
            </table>
          )}
        </Section>
      </div>

      {/* ======= Map + Scrubber ======= */}
      <div className="dev-right-panel">
        <div className="dev-map-area">
          <MapContainer center={center} zoom={15} style={{ height: '100%', width: '100%' }} maxZoom={30} zoomDelta={0.2} zoomSnap={0.2}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              maxZoom={30}
            />
            <MapResizeHandler />
            <MapFitter bounds={mapBounds} />

            {/* KMZ / KML overlays (under activity traces) */}
            {kmzOverlays && kmzOverlays
              .filter(overlay => overlay.visible)
              .flatMap(overlay => overlay.groups
                .filter(group => group.visible)
                .map(group => (
                  <GeoJSON
                    key={`kmz-dev-${group.id}`}
                    data={{ type: 'FeatureCollection', features: group.features }}
                    style={(feature) => {
                      const props = feature.properties || {};
                      const geomType = feature.geometry?.type || '';
                      const opacity = overlay.opacity;
                      if (geomType.includes('Polygon')) {
                        return {
                          color: props['stroke'] || KMZ_STYLE_DEFAULTS.polygon.color,
                          weight: props['stroke-width'] || KMZ_STYLE_DEFAULTS.polygon.weight,
                          opacity: (props['stroke-opacity'] ?? KMZ_STYLE_DEFAULTS.polygon.opacity) * opacity,
                          fillColor: props['fill'] || KMZ_STYLE_DEFAULTS.polygon.fillColor,
                          fillOpacity: (props['fill-opacity'] ?? KMZ_STYLE_DEFAULTS.polygon.fillOpacity) * opacity,
                        };
                      }
                      return {
                        color: props['stroke'] || KMZ_STYLE_DEFAULTS.line.color,
                        weight: props['stroke-width'] || KMZ_STYLE_DEFAULTS.line.weight,
                        opacity: (props['stroke-opacity'] ?? KMZ_STYLE_DEFAULTS.line.opacity) * opacity,
                      };
                    }}
                    pointToLayer={(feature, latlng) => {
                      const props = feature.properties || {};
                      const opacity = overlay.opacity;
                      return L.circleMarker(latlng, {
                        ...KMZ_STYLE_DEFAULTS.point,
                        color: props['stroke'] || KMZ_STYLE_DEFAULTS.point.color,
                        fillColor: props['fill'] || KMZ_STYLE_DEFAULTS.point.fillColor,
                        fillOpacity: KMZ_STYLE_DEFAULTS.point.fillOpacity * opacity,
                        opacity,
                      });
                    }}
                    onEachFeature={(feature, layer) => {
                      const name = feature.properties?.name;
                      if (name) layer.bindTooltip(name, { sticky: true });
                    }}
                  />
                ))
              )}

            {Array.from(selectedIds).map(id => {
              const data = displayData[id];
              if (!data) return null;
              const color = getColor(id);

              return (
                <React.Fragment key={id}>
                  {showRawTrace && data.raw.length > 1 && (
                    <Polyline
                      positions={data.raw.map(p => [p.latitude, p.longitude])}
                      color={color}
                      weight={3}
                      opacity={0.3}
                      dashArray="6 4"
                    />
                  )}
                  {showSmoothedTrace && data.smoothed.length > 1 && (
                    <Polyline
                      positions={data.smoothed.map(p => [p.latitude, p.longitude])}
                      color={color}
                      weight={4}
                      opacity={0.9}
                    />
                  )}
                  {showRawPoints && data.raw.map((p) => {
                    const accColor = showAccuracyColors
                      ? getAccuracyColor(p.accuracy, accuracyBounds.min, accuracyBounds.max)
                      : null;
                    return (
                      <CircleMarker
                        key={`raw-${id}-${p.timestamp}`}
                        center={[p.latitude, p.longitude]}
                        radius={markerSize}
                        color={accColor || '#888'}
                        fillColor={accColor || '#aaa'}
                        fillOpacity={accColor ? 0.85 : 0.6}
                        weight={1}
                      >
                        <Tooltip>
                          Raw &middot; {id.substring(0, 10)}<br />
                          Acc: {p.accuracy != null ? p.accuracy.toFixed(1) : 'N/A'} m<br />
                          {new Date(p.timestamp).toLocaleTimeString()}
                        </Tooltip>
                      </CircleMarker>
                    );
                  })}
                  {showSmoothedPoints && data.smoothed.map((p) => (
                    <CircleMarker
                      key={`smooth-${id}-${p.timestamp}`}
                      center={[p.latitude, p.longitude]}
                      radius={markerSize}
                      color={color}
                      fillColor={color}
                      fillOpacity={0.8}
                      weight={1}
                    >
                      <Tooltip>
                        Smoothed &middot; {id.substring(0, 10)}<br />
                        {new Date(p.timestamp).toLocaleTimeString()}
                      </Tooltip>
                    </CircleMarker>
                  ))}
                </React.Fragment>
              );
            })}
          </MapContainer>

          {selectedIds.size > 0 && (
            <div className="dev-map-legend">
              {Array.from(selectedIds).map(id => (
                <div key={id} className="legend-activity-group">
                  <span className="legend-activity-name" style={{ color: getColor(id) }}>
                    {id.substring(0, 12)}
                  </span>
                  <div className="legend-row">
                    <span className="legend-line dashed" style={{ borderColor: getColor(id) }} />
                    <span>Raw</span>
                  </div>
                  <div className="legend-row">
                    <span className="legend-line solid" style={{ backgroundColor: getColor(id) }} />
                    <span>Processed</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showAccuracyColors && (
            <div className="dev-accuracy-legend">
              <AccuracyLegend
                minAccuracy={accuracyBounds.min}
                maxAccuracy={accuracyBounds.max}
                onBoundsChange={handleAccuracyBoundsChange}
              />
            </div>
          )}
        </div>

        {selectedIds.size > 0 && (
          <div className="dev-scrubber-wrapper">
            <Scrubber
              head={timeHead}
              tail={timeTail}
              onHeadChange={setTimeHead}
              onTailChange={setTimeTail}
              globalTimeRange={globalTimeRange}
              showPointMarkers={showRawPoints}
              onTogglePointMarkers={() => setShowRawPoints(p => !p)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default DevPage;
